import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';

type TableCellSize = 'sm' | 'md';

/**
 * A layout container for a single data table cell.
 *
 * Renders as a focusable, interactive `gridcell` within a data grid. Supports
 * `sm`/`md` size variants, selected state, disabled state, and hover interaction.
 * Fully presentational — selection management and focus roving belong in the
 * parent `asc-table` widget, which controls `tabindex` via the roving-tabindex
 * pattern.
 *
 * @element asc-table-cell
 *
 * @fires asc-activate - Fired when the cell is activated by pointer click or
 *   keyboard (`Enter`/`Space`) and the cell is not disabled.
 *   Detail: `{ originalEvent: MouseEvent | KeyboardEvent }`
 *
 * @slot - The primary content area. Accepts text, avatars, icons, badges,
 *   payment indicators, or any inline element.
 *
 * @csspart base - The inner wrapper `<div>`. Primary style hook for padding,
 *   background, alignment, and overflow.
 *
 * @cssprop --asc-table-cell-padding-x-sm - Horizontal padding when `size="sm"`. Default: `var(--asc-spacing-2xl)`
 * @cssprop --asc-table-cell-padding-y-sm - Vertical padding when `size="sm"`. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-table-cell-padding-x-md - Horizontal padding when `size="md"`. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-table-cell-padding-y-md - Vertical padding when `size="md"`. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-table-cell-min-width-sm - Minimum cell width when `size="sm"`. Default: `0px`
 * @cssprop --asc-table-cell-min-width-md - Minimum cell width when `size="md"`. Default: `0px`
 * @cssprop --asc-table-cell-bg - Background in the resting/default state. Default: `transparent`
 * @cssprop --asc-table-cell-bg-hover - Background when hovered and not selected. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-table-cell-bg-selected - Background when `selected` is `true`. Default: `var(--asc-bg-secondary)`
 * @cssprop --asc-table-cell-bg-selected-hover - Background when `selected` and hovered. Default: `var(--asc-bg-secondary_hover)`
 * @cssprop --asc-table-cell-bg-disabled - Background when `disabled` is `true`. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-table-cell-color-disabled - Text/icon color when `disabled` is `true`. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-table-cell-border-bottom-color - Color of the bottom row separator. Default: `var(--asc-border-secondary)`. Set to `transparent` to remove.
 * @cssprop --asc-table-cell-border-bottom-width - Width of the bottom row separator. Default: `1px`
 * @cssprop --asc-table-cell-transition-duration - Speed of background transitions. Default: `var(--asc-transition-fast)`
 */
@customElement('asc-table-cell')
export class AscTableCell extends LitElement {
  static override styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      cursor: default;
      /* Fills the full <td> height so the selected background covers the whole row */
      height: 100%;
    }

    :host(:focus-visible) {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    .base {
      display: flex;
      align-items: center;
      overflow: hidden;
      height: 100%;
      box-sizing: border-box;
      background-color: var(--asc-table-cell-bg, transparent);
      border-bottom:
        var(--asc-table-cell-border-bottom-width, 1px)
        solid
        var(--asc-table-cell-border-bottom-color, var(--asc-border-secondary));
      transition: background-color
        var(--asc-table-cell-transition-duration, var(--asc-transition-fast));
    }

    /* ─── Size: sm ────────────────────────────────────────────── */
    :host([size='sm']) .base {
      padding: var(--asc-table-cell-padding-y-sm, var(--asc-spacing-xl))
        var(--asc-table-cell-padding-x-sm, var(--asc-spacing-2xl));
      min-width: var(--asc-table-cell-min-width-sm, 0px);
    }

    /* ─── Size: md (default) ──────────────────────────────────── */
    :host([size='md']) .base,
    :host(:not([size])) .base {
      padding: var(--asc-table-cell-padding-y-md, var(--asc-spacing-xl))
        var(--asc-table-cell-padding-x-md, var(--asc-spacing-3xl));
      min-width: var(--asc-table-cell-min-width-md, 0px);
    }

    /* ─── Hover ───────────────────────────────────────────────── */
    :host(:hover:not([disabled])) .base {
      background-color: var(
        --asc-table-cell-bg-hover,
        var(--asc-bg-primary_hover)
      );
    }

    /* ─── Selected ────────────────────────────────────────────── */
    :host([selected]) .base {
      background-color: var(--asc-table-cell-bg-selected, var(--asc-bg-secondary));
    }

    /* ─── Selected + hover ────────────────────────────────────── */
    :host([selected]:hover:not([disabled])) .base {
      background-color: var(
        --asc-table-cell-bg-selected-hover,
        var(--asc-bg-secondary_hover)
      );
    }

    /* ─── Disabled ────────────────────────────────────────────── */
    :host([disabled]) {
      pointer-events: none;
    }

    :host([disabled]) .base {
      background-color: var(
        --asc-table-cell-bg-disabled,
        var(--asc-bg-disabled_subtle)
      );
      color: var(--asc-table-cell-color-disabled, var(--asc-text-disabled));
    }
  `;

  /** Controls internal padding and min-width. `sm` is the compact variant. */
  @property({ type: String, reflect: true }) size: TableCellSize = 'md';

  /** When `true`, renders with a highlighted background to indicate selection. */
  @property({ type: Boolean, reflect: true }) selected = false;

  /** When `true`, suppresses interaction and visually mutes the cell. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('role', 'gridcell');
    this.tabIndex = -1;
    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._handleKeyDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keydown', this._handleKeyDown);
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    if (changed.has('selected')) {
      this.setAttribute('aria-selected', String(this.selected));
    }
    if (changed.has('disabled')) {
      if (this.disabled) {
        this.setAttribute('aria-disabled', 'true');
        this.tabIndex = -1;
      } else {
        this.removeAttribute('aria-disabled');
      }
    }
  }

  private readonly _handleClick = (e: MouseEvent): void => {
    if (this.disabled) return;
    dispatch<{ originalEvent: MouseEvent }>(this, 'asc-activate', {
      detail: { originalEvent: e },
    });
  };

  private readonly _handleKeyDown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      dispatch<{ originalEvent: KeyboardEvent }>(this, 'asc-activate', {
        detail: { originalEvent: e },
      });
    }
  };

  override render() {
    return html`
      <div part="base" class="base">
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-table-cell': AscTableCell;
  }
}
